import { computed, reactive } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { createPersistedJson } from '~/utils/persistedJson'
import {
  hydratedPlanningScope,
  importedScopeOf,
  planningScopeStorageKey,
} from '~/utils/planningImportScope'

/**
 * Module-level singleton so the store getter, the planning bar and the import
 * menu all share one source of truth. `scope` is the person whose planning the
 * owner is VIEWING (read-only): `{ userId, label, color }` — the peer's own
 * display identity (User.label/color; the retired user tag is gone) — or `null`.
 * The storage key, the stored shape and the hydration rule live in
 * `utils/planningImportScope`.
 */
const state = reactive({
  userId: null,
  scope: null,
})

/** (Re)hydrate `state.scope` from localStorage for `userId`. */
function sync(userId) {
  state.scope = hydratedPlanningScope(createPersistedJson(planningScopeStorageKey(userId), null).read())
  state.userId = userId
}

function persist() {
  createPersistedJson(planningScopeStorageKey(state.userId), null).write(state.scope)
}

/**
 * The user whose planning is being VIEWED (view-as), or null when you're on your
 * own planning. The store fetches THIS user's plans and patches `myPlan` so the
 * planning bar shows their days read-only.
 */
export function readViewAsUserId() {
  return state.scope?.userId ?? null
}

/**
 * Per-user, client-only "View As @X" scope, set when picking a peer from the
 * Follow-Ups nav right-click menu (see ViewAsNavMenu). Read-only — nothing is
 * copied. Persisted in localStorage; cleared via the menu's "My View".
 */
export function usePlanningImportScope() {
  const authStore = useAuthStore()
  const userId = authStore.authenticatedUser?.id ?? 'anon'

  // First use or a user switch → (re)hydrate from storage.
  if (state.userId !== userId) sync(userId)

  const importedScope = computed(() => state.scope)

  // The "Viewing @X ✕" chip shows whenever a scope is set; the ✕ clears it.
  const activeImportedScope = computed(() => state.scope)

  const viewAsUserId = computed(() => state.scope?.userId ?? null)

  /** Start viewing a member's planning (call when picking a peer). */
  function setImportedScope(scope = {}) {
    state.scope = importedScopeOf(scope)
    persist()
  }

  /** Back to your own planning (the ✕ on the chip). */
  function clearImportedScope() {
    state.scope = null
    persist()
  }

  return {
    importedScope,
    activeImportedScope,
    viewAsUserId,
    setImportedScope,
    clearImportedScope,
  }
}
